/**
 * Three.js auto-detection module.
 * Hooks into __THREE_DEVTOOLS__ to catch scenes, renderers, and cameras
 * as Three.js registers them. Works with any Three.js application.
 *
 * Based on the Chrome extension's needle-chrome.client.js detection pattern.
 */

export interface ThreeDetectionHost {
    readonly scenes: object[];
    readonly renderers: object[];
    readonly cameras: object[];
    readonly versions: string[];
    onChanged(cb: (event: ThreeDetectionEvent) => void): () => void;
}

export interface ThreeDetectionEvent {
    type: "added" | "removed";
    object: any;
}

declare global {
    interface Window {
        __THREE_DEVTOOLS__?: {
            dispatchEvent: (event: any) => void;
            addEventListener?: (event: string, callback: Function) => void;
            removeEventListener?: (event: string, callback: Function) => void;
        };
        __NEEDLE_EXTENSION_HOST__?: ThreeDetectionHost;
        __NEEDLE_INSPECTOR__?: any;
    }
}

/**
 * Hook into Three.js devtools events to detect scenes, renderers, and cameras.
 * Three.js dispatches 'observe' events for scenes and renderers when they are created,
 * and 'register' events for version information.
 *
 * Returns a host object that tracks all detected Three.js objects.
 */
export function setupThreeDetection(): ThreeDetectionHost {

    // If host already exists (e.g. Chrome extension already set it up), reuse it
    if (window.__NEEDLE_EXTENSION_HOST__) {
        return window.__NEEDLE_EXTENSION_HOST__;
    }

    const onChangeListeners: Array<(event: ThreeDetectionEvent) => void> = [];

    const host: ThreeDetectionHost = {
        scenes: [],
        renderers: [],
        cameras: [],
        versions: [],
        onChanged(callback) {
            onChangeListeners.push(callback);
            return () => {
                const index = onChangeListeners.indexOf(callback);
                if (index >= 0) onChangeListeners.splice(index, 1);
            };
        },
    };

    // Preserve any existing __THREE_DEVTOOLS__ methods
    const originalDispatchEvent = window.__THREE_DEVTOOLS__?.dispatchEvent;
    const originalAddEventListener = window.__THREE_DEVTOOLS__?.addEventListener;
    const originalRemoveEventListener = window.__THREE_DEVTOOLS__?.removeEventListener;

    window.__THREE_DEVTOOLS__ = window.__THREE_DEVTOOLS__ || {
        addEventListener(event: string, callback: Function) {
            originalAddEventListener?.call(this, event, callback);
        },
        removeEventListener(event: string, callback: Function) {
            originalRemoveEventListener?.call(this, event, callback);
        },
        dispatchEvent(evt: any) {
            originalDispatchEvent?.call(this, evt);
        },
    };

    window.__THREE_DEVTOOLS__.dispatchEvent = function (event: any) {
        switch (event.type) {
            case "observe":
                if (event.detail?.isScene) {
                    const scene = event.detail;
                    if (!host.scenes.includes(scene)) {
                        host.scenes.push(scene);
                        onChangeListeners.forEach(cb => cb({ type: "added", object: scene }));
                    }

                    // Hook into onBeforeRender to catch cameras and renderers.
                    // We save and restore the original to avoid interfering with
                    // the host engine's render loop (e.g. Needle Engine's
                    // pre_update_oneshot_callbacks).
                    const origOnBeforeRender = scene.onBeforeRender;
                    scene.onBeforeRender = function (renderer: any, _scene: any, camera: any) {
                        if (!host.cameras.includes(camera)) {
                            host.cameras.push(camera);
                            onChangeListeners.forEach(cb => cb({ type: "added", object: camera }));
                        }
                        if (!host.renderers.includes(renderer)) {
                            host.renderers.push(renderer);
                            onChangeListeners.forEach(cb => cb({ type: "added", object: renderer }));
                        }
                        // Restore original after first capture to stop interfering
                        scene.onBeforeRender = origOnBeforeRender;
                        if (origOnBeforeRender) {
                            origOnBeforeRender.call(scene, renderer, _scene, camera);
                        }
                    };
                }
                else if (event.detail?.isWebGLRenderer) {
                    const renderer = event.detail;
                    if (!host.renderers.includes(renderer)) {
                        host.renderers.push(renderer);
                        onChangeListeners.forEach(cb => cb({ type: "added", object: renderer }));
                    }
                }
                break;

            case "register":
                const version = event.detail?.revision || event.detail?.version || event.detail;
                if (version && !host.versions.includes(version)) {
                    host.versions.push(version);
                }
                break;
        }

        originalDispatchEvent?.call(this, event);
    };

    window.__NEEDLE_EXTENSION_HOST__ = host;

    // Late-injection fallback: if the script loads after Three.js already
    // dispatched its observe events, scan for existing renderers via canvas elements.
    // Three.js WebGLRenderer stores a back-reference on its canvas as `__r`.
    scanForExistingRenderers(host, onChangeListeners);

    return host;
}

/**
 * Fallback scene discovery for late injection.
 * Walks all canvas elements looking for Three.js renderers that already exist.
 * Three.js WebGLRenderer stores itself on `canvas.__r` (internal field) or
 * we can check for `canvas.getContext('webgl2')` patterns.
 *
 * Also checks common globals like `window.renderer`, `window.scene`.
 */
function scanForExistingRenderers(
    host: ThreeDetectionHost,
    listeners: Array<(event: ThreeDetectionEvent) => void>,
) {
    function addIfNew(obj: any, list: object[], type: string) {
        if (obj && !list.includes(obj)) {
            list.push(obj);
            listeners.forEach(cb => cb({ type: "added", object: obj }));
            return true;
        }
        return false;
    }

    function addRenderer(renderer: any) {
        if (!renderer?.isWebGLRenderer) return;
        addIfNew(renderer, host.renderers, "renderer");

        // Try to extract scene from renderer's render info
        // Three.js renderers have an internal _renderScene or we can
        // hook the next render call to catch the scene
        const origRender = renderer.render;
        if (origRender && !renderer.__needle_hooked) {
            renderer.__needle_hooked = true;
            renderer.render = function (scene: any, camera: any) {
                if (scene?.isScene) addIfNew(scene, host.scenes, "scene");
                if (camera?.isCamera) addIfNew(camera, host.cameras, "camera");
                // Restore original after first capture
                renderer.render = origRender;
                return origRender.call(this, scene, camera);
            };
        }
    }

    function addScene(scene: any) {
        if (!scene?.isScene) return;
        addIfNew(scene, host.scenes, "scene");
    }

    // Check common global patterns
    const win = window as any;
    if (win.renderer) addRenderer(win.renderer);
    if (win.scene) addScene(win.scene);

    // Scan canvas elements for Three.js renderers (including shadow DOM)
    function scanCanvas(canvas: any) {
        if (canvas.__r) addRenderer(canvas.__r);
        if (canvas.__three_renderer) addRenderer(canvas.__three_renderer);
    }

    // Document-level canvases
    document.querySelectorAll('canvas').forEach(scanCanvas);

    // Shadow DOM canvases (e.g. <needle-engine> uses shadow DOM)
    document.querySelectorAll('needle-engine').forEach((el: any) => {
        if (el.shadowRoot) {
            el.shadowRoot.querySelectorAll('canvas').forEach(scanCanvas);
        }
    });

    // Check Needle Engine instances via Context
    // Needle Engine exposes contexts as window['NeedleEngine.Context.Current']
    // or window.Needle.Context, not window.Needle.Engine
    const ctx = win['NeedleEngine.Context.Current'] || win.Needle?.Context?.Current;
    if (ctx) {
        if (ctx.renderer) addRenderer(ctx.renderer);
        if (ctx.scene) addScene(ctx.scene);
    }

    // Also check all Needle Engine context instances
    const ContextClass = win.Needle?.Context || win.NeedleEngine?.Context;
    if (ContextClass) {
        const instances = ContextClass.Current ? [ContextClass.Current] :
            (ContextClass.Instances || ContextClass.instances || []);
        if (Array.isArray(instances)) {
            for (const instance of instances) {
                if (instance.renderer) addRenderer(instance.renderer);
                if (instance.scene) addScene(instance.scene);
            }
        }
    }
}
